import * as THREE from 'three';
import {
  DEFAULT_HERO_IMAGE_CONFIG,
  createSceneLoadQueue,
  prepareSceneImages,
} from './scene-image-preparation.js';
import { SceneTextureCache } from './scene-texture-cache.js';
import {
  DEFAULT_TUNNEL_CONFIG,
  advanceTunnelLoopOffset,
  createSeparatedTunnelSlot,
  getContinuousTunnelCycle,
  getContinuousTunnelProgress,
  getContinuousTunnelZ,
  getIntroCameraZ,
  getIntroLoopSpeedMultiplier,
  getIntroSpeedMultiplier,
  getLoopedTunnelDepthProgress,
  getTunnelVisualState,
} from './tunnel-motion.js';
import { getPortraitPlaneAspect } from './portrait-plane.js';

export class HeroImageScene {
  constructor(root, backendImages = [], options = {}) {
    this.root = typeof root === 'string' ? document.querySelector(root) : root;
    this.canvas =
      options.canvas ??
      this.root?.querySelector('.first-sec__floema-floating-canvas') ??
      this.root?.querySelector('.hero-scene__canvas');
    this.backendImages = backendImages;
    this.config = { ...DEFAULT_HERO_IMAGE_CONFIG, ...options };
    this.tunnel = { ...DEFAULT_TUNNEL_CONFIG, ...(options.tunnel ?? {}) };
    this.portrait = options.portrait ?? {};
    this.textureCache = options.textureCache ?? new SceneTextureCache();
    this.ownsTextureCache = options.ownsTextureCache ?? !options.textureCache;
    this.gsap = options.gsap ?? globalThis.gsap;
    this.sceneImages = [];
    this.meshes = new Map();
    this.texturePool = [];
    this.texturePoolKeys = new Set();
    this.lazyTimers = [];
    this.textureRebalanceTimer = null;
    this.needsTextureRebalance = false;
    this.clock = new THREE.Clock(false);
    this.reducedMotion =
      globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    this.isStarted = false;
    this.motionStarted = false;
    this.isDestroyed = false;
    this.animationFrame = 0;
    this.recycleCycle = 1;
    this.motionElapsed = 0;
    this.loopOffset = 0;
    this.handleResize = this.handleResize.bind(this);
    this.render = this.render.bind(this);
  }

  start() {
    if (!this.root || !this.canvas || this.isStarted) {
      return this;
    }

    this.sceneImages = prepareSceneImages(this.backendImages, this.config);

    if (!this.sceneImages.length) {
      return this;
    }

    this.isStarted = true;
    this.createRenderer();
    this.createScene();
    this.handleResize();
    globalThis.addEventListener?.('resize', this.handleResize);
    this.render();
    this.loadImages();
    this.root.dataset.sceneReady = 'true';

    if (this.config.autoPlay !== false) {
      this.play();
    }

    return this;
  }

  play() {
    if (!this.isStarted || this.motionStarted || this.isDestroyed) {
      return this;
    }

    this.motionStarted = true;
    this.motionElapsed = 0;
    this.clock.start();

    if (this.camera) {
      this.camera.position.z = this.reducedMotion
        ? 0
        : getIntroCameraZ(0, this.tunnel);
    }

    for (const mesh of this.meshes.values()) {
      this.revealMesh(mesh);
    }

    if (this.needsTextureRebalance) {
      this.requestTextureRebalance();
    }

    return this;
  }

  destroy() {
    this.isDestroyed = true;
    globalThis.removeEventListener?.('resize', this.handleResize);
    globalThis.cancelAnimationFrame?.(this.animationFrame);

    for (const timer of this.lazyTimers) {
      clearTimeout(timer);
    }

    clearTimeout(this.textureRebalanceTimer);
    this.lazyTimers = [];
    this.textureRebalanceTimer = null;

    for (const mesh of this.meshes.values()) {
      this.gsap?.killTweensOf?.(mesh.userData);
      this.scene?.remove(mesh);
      mesh.material.dispose();
    }

    this.meshes.clear();
    this.texturePool = [];
    this.texturePoolKeys.clear();
    this.geometry?.dispose();
    this.renderer?.dispose();

    if (this.ownsTextureCache) {
      this.textureCache.dispose();
    }
  }

  createRenderer() {
    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      alpha: true,
      antialias: true,
      powerPreference: 'high-performance',
    });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
  }

  createScene() {
    this.scene = new THREE.Scene();
    this.camera = new THREE.PerspectiveCamera(50, 1, 0.1, 260);
    this.camera.position.set(0, 0, getIntroCameraZ(0, this.tunnel));
    this.camera.lookAt(0, 0, -1);
    this.geometry = new THREE.PlaneGeometry(1, 1, 1, 1);
  }

  handleResize() {
    if (!this.renderer || !this.camera) {
      return;
    }

    const width = Math.max(1, this.root.clientWidth || globalThis.innerWidth || 1);
    const height = Math.max(1, this.root.clientHeight || globalThis.innerHeight || 1);
    const viewport = getHeroRenderViewport(width, height, this.config);
    const pixelRatio = Math.min(
      globalThis.devicePixelRatio || 1,
      this.config.pixelRatioCap ?? 2
    );

    this.root.style.setProperty(
      '--hero-render-bleed-bottom',
      `${viewport.bleedBottom}px`
    );
    this.root.style.setProperty('--hero-render-bleed-x', `${viewport.bleedX}px`);
    this.camera.aspect = viewport.renderWidth / viewport.contentHeight;
    this.camera.setViewOffset(
      viewport.renderWidth,
      viewport.contentHeight,
      0,
      0,
      viewport.renderWidth,
      viewport.renderHeight
    );
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(pixelRatio);
    this.renderer.setSize(viewport.renderWidth, viewport.renderHeight, false);
  }

  loadImages() {
    const queue = createSceneLoadQueue(this.sceneImages, this.config);
    this.loadBatch(queue.initial, 0);

    queue.lazy.forEach((batch, index) => {
      const timer = globalThis.setTimeout(() => {
        this.loadBatch(batch, index + 1);
      }, this.config.lazyLoadDelay * (index + 1));

      this.lazyTimers.push(timer);
    });
  }

  async loadBatch(batch, batchIndex) {
    const results = await Promise.allSettled(
      batch.map(async (item, index) => {
        const texture = await this.textureCache.load(item.resource);

        return { item, texture, index };
      })
    );

    for (const result of results) {
      if (result.status === 'fulfilled' && !this.isDestroyed) {
        this.addToTexturePool(result.value.item, result.value.texture);
      }
    }

    if (batchIndex === 0) {
      this.ensureLoopMeshes();
    } else {
      this.ensureLoopMeshes();
      if (!this.shouldStageUniqueMeshCreation()) {
        this.requestTextureRebalance();
      }
    }
  }

  addToTexturePool(item, texture) {
    const key = item.textureKey ?? item.resource?.textureKey;

    if (!key || this.texturePoolKeys.has(key)) {
      return;
    }

    this.texturePoolKeys.add(key);
    this.texturePool.push({
      item,
      textureKey: key,
      texture,
      aspectRatio: getPortraitPlaneAspect(
        getTextureAspectRatio(texture, item.aspectRatio),
        this.portrait
      ),
    });
  }

  ensureLoopMeshes() {
    if (this.texturePool.length === 0) {
      return;
    }

    if (!this.meshes) {
      this.meshes = new Map();
    }

    if (!this.sceneImages?.length) {
      return;
    }

    if (this.meshes.size === 0) {
      this.motionElapsed = 0;
      this.loopOffset = 0;
    }

    const poolByItemId = this.getTexturePoolByItemId();
    const items = this.getLoopMeshItems(poolByItemId);

    items.forEach((item) => {
      if (this.meshes.has(item.id)) {
        return;
      }

      const poolEntry =
        poolByItemId.get(item.id) ?? this.pickTextureForSlot(item.slotIndex, 0);
      this.addTunnelPlane(item, poolEntry);
    });
  }

  getLoopMeshItems(poolByItemId = this.getTexturePoolByItemId()) {
    if (!this.shouldStageUniqueMeshCreation()) {
      return this.sceneImages;
    }

    return this.sceneImages.filter((item) => poolByItemId.has(item.id));
  }

  addTunnelPlane(item, poolEntry) {
    if (!poolEntry) {
      return;
    }

    const texture = poolEntry.texture;
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      transparent: true,
      opacity: 0,
      depthTest: true,
      depthWrite: false,
      side: THREE.DoubleSide,
      toneMapped: false,
    });
    const mesh = new THREE.Mesh(this.geometry, material);
    const aspectRatio = getPortraitPlaneAspect(
      getTextureAspectRatio(texture, item.aspectRatio),
      this.portrait
    );
    const planeAspect = poolEntry.aspectRatio ?? aspectRatio;
    const { slot, depthPhase } = this.createInitialSlot(item, planeAspect);

    mesh.userData.item = item;
    mesh.userData.textureItem = poolEntry.item;
    mesh.userData.slot = slot;
    mesh.userData.aspectRatio = planeAspect;
    mesh.userData.reveal = 0;
    mesh.userData.recycleReveal = 1;
    mesh.userData.loopSlotIndex = item.slotIndex;
    mesh.userData.depthPhase = depthPhase;
    mesh.userData.loopCycle = getContinuousTunnelCycle(depthPhase, this.loopOffset);
    this.applyPlaneGeometry(mesh);
    this.scene.add(mesh);
    this.meshes.set(item.id, mesh);

    if (this.motionStarted) {
      this.revealMesh(mesh);
    }
  }

  createInitialSlot(item, aspectRatio = 1) {
    const depthProgress = getLoopedTunnelDepthProgress(
      item.slotIndex,
      this.sceneImages.length,
      {
        seed: this.tunnel.seed,
        loopDepthJitter: this.tunnel.loopDepthJitter,
      }
    );
    const z = getContinuousTunnelZ(depthProgress, this.tunnel);
    const slot = createSeparatedTunnelSlot(item.slotIndex, this.getOccupiedSlots(), {
      ...this.tunnel,
      cameraAspect: this.camera.aspect,
      fixedZ: z,
      projectedPlaneAspect: aspectRatio,
    });

    return { slot, depthPhase: depthProgress };
  }

  render() {
    if (this.isDestroyed) {
      return;
    }

    this.animationFrame = globalThis.requestAnimationFrame?.(this.render) ?? 0;
    const delta = Math.min(this.clock.getDelta(), 0.05);
    this.updateTunnel(delta);
    this.updateCameraIntro();
    this.renderer.render(this.scene, this.camera);
  }

  updateTunnel(delta) {
    if (this.meshes.size === 0 || !this.motionStarted) {
      return;
    }

    this.motionElapsed += delta;

    const speedMultiplier = this.reducedMotion
      ? 0.18
      : getIntroSpeedMultiplier(this.motionElapsed, this.tunnel) *
        getIntroLoopSpeedMultiplier(this.motionElapsed, this.tunnel);
    this.loopOffset = advanceTunnelLoopOffset(this.loopOffset, delta, {
      ...this.tunnel,
      speedMultiplier,
    });

    for (const mesh of this.meshes.values()) {
      const slot = mesh.userData.slot;
      const progress = getContinuousTunnelProgress(
        mesh.userData.depthPhase,
        this.loopOffset
      );
      const cycle = getContinuousTunnelCycle(
        mesh.userData.depthPhase,
        this.loopOffset
      );

      if (cycle !== mesh.userData.loopCycle) {
        this.recyclePlane(mesh, cycle);
        mesh.userData.loopCycle = cycle;
      }

      slot.z = getContinuousTunnelZ(progress, this.tunnel);
      this.applyPlaneGeometry(mesh);
    }
  }

  updateCameraIntro() {
    if (!this.motionStarted || !this.camera) {
      return;
    }

    this.camera.position.z = this.reducedMotion
      ? 0
      : getIntroCameraZ(this.motionElapsed, this.tunnel);
  }

  recyclePlane(mesh, cycle) {
    const keepTexture = this.shouldKeepMeshTextureOnRecycle();
    const next = keepTexture
      ? null
      : this.pickTextureForSlot(
          mesh.userData.loopSlotIndex,
          cycle,
          mesh.material.map,
          mesh
        );
    const progress = getContinuousTunnelProgress(
      mesh.userData.depthPhase,
      this.loopOffset
    );
    const z = getContinuousTunnelZ(progress, this.tunnel);

    this.recycleCycle += 1;
    const nextAspectRatio = keepTexture
      ? mesh.userData.aspectRatio ?? 1
      : next?.aspectRatio ?? mesh.userData.aspectRatio ?? 1;

    Object.assign(
      mesh.userData.slot,
      createSeparatedTunnelSlot(
        mesh.userData.loopSlotIndex + this.recycleCycle * 997,
        this.getOccupiedSlots(mesh),
        {
          ...this.tunnel,
          cameraAspect: this.camera.aspect,
          fixedZ: z,
          projectedPlaneAspect: nextAspectRatio,
        }
      )
    );

    this.gsap?.killTweensOf?.(mesh.userData, 'recycleReveal');
    mesh.userData.recycleReveal = 1;

    if (next) {
      mesh.userData.textureItem = next.item;
      mesh.userData.aspectRatio = nextAspectRatio;
      mesh.material.map = next.texture;
      mesh.material.needsUpdate = true;
    }
  }

  pickTextureForSlot(slotIndex, cycle = 0, excludedTexture = null, excludedMesh = null) {
    const length = this.texturePool.length;

    if (length === 0) {
      return null;
    }

    const initialIndex = mixInteger(slotIndex * 73856093 + cycle * 19349663) % length;
    const usageCounts = this.getTextureUsageCounts(excludedMesh);
    const repeatLimit = this.getTextureRepeatLimit();
    const isUnderRepeatLimit = (entry) => {
      const key = getTextureEntryKey(entry);

      return !key || (usageCounts.get(key) ?? 0) < repeatLimit;
    };
    const canUseCandidate = (entry) => {
      if (!entry) {
        return false;
      }

      if (excludedTexture && length > 1 && entry.texture === excludedTexture) {
        return false;
      }

      return isUnderRepeatLimit(entry);
    };

    if (canUseCandidate(this.texturePool[initialIndex])) {
      return this.texturePool[initialIndex];
    }

    for (let offset = 1; offset < length; offset += 1) {
      const candidateIndex = (initialIndex + offset) % length;

      if (canUseCandidate(this.texturePool[candidateIndex])) {
        return this.texturePool[candidateIndex];
      }
    }

    const excludedEntry = excludedTexture
      ? this.texturePool.find((entry) => entry.texture === excludedTexture)
      : null;

    if (excludedEntry && isUnderRepeatLimit(excludedEntry)) {
      return excludedEntry;
    }

    let index = initialIndex;

    if (
      excludedTexture &&
      length > 1 &&
      this.texturePool[index]?.texture === excludedTexture
    ) {
      for (let offset = 1; offset < length; offset += 1) {
        const candidateIndex = (index + offset) % length;

        if (this.texturePool[candidateIndex]?.texture !== excludedTexture) {
          index = candidateIndex;
          break;
        }
      }
    }

    return this.texturePool[index];
  }

  getTextureRepeatLimit() {
    const uniqueMax = toPositiveInteger(
      this.config?.duplicateTextureUniqueMax,
      DEFAULT_HERO_IMAGE_CONFIG.duplicateTextureUniqueMax
    );
    const strictMinPoolSize = toPositiveInteger(
      this.config?.duplicateTextureStrictMinPoolSize,
      DEFAULT_HERO_IMAGE_CONFIG.duplicateTextureStrictMinPoolSize
    );
    const strictMax = toPositiveInteger(
      this.config?.duplicateTextureStrictMax,
      DEFAULT_HERO_IMAGE_CONFIG.duplicateTextureStrictMax
    );
    const relaxedMax = toPositiveInteger(
      this.config?.duplicateTextureRelaxedMax,
      DEFAULT_HERO_IMAGE_CONFIG.duplicateTextureRelaxedMax
    );
    const activeSceneCount = Math.max(1, this.sceneImages?.length ?? 0);

    if (this.canLoadedPoolCoverActiveUniqueMeshes()) {
      return uniqueMax;
    }

    if (this.texturePool.length >= activeSceneCount) {
      return uniqueMax;
    }

    return this.texturePool.length >= strictMinPoolSize ? strictMax : relaxedMax;
  }

  canLoadedPoolCoverActiveUniqueMeshes() {
    const activeMeshCount = Math.max(1, this.meshes?.size ?? 0);

    return (
      this.shouldStageUniqueMeshCreation() &&
      this.texturePool.length >= activeMeshCount
    );
  }

  getTextureUsageCounts(excludedMesh = null) {
    const counts = new Map();

    for (const mesh of this.meshes.values()) {
      if (mesh === excludedMesh) {
        continue;
      }

      const key = getMeshTextureKey(mesh, this.texturePool);

      if (key) {
        counts.set(key, (counts.get(key) ?? 0) + 1);
      }
    }

    return counts;
  }

  getTexturePoolByItemId() {
    return new Map(
      this.texturePool
        .map((entry) => [entry.item?.id, entry])
        .filter(([id]) => Boolean(id))
    );
  }

  shouldStageUniqueMeshCreation() {
    if (!this.sceneImages?.length) {
      return false;
    }

    return getUniqueSceneTextureCount(this.sceneImages) >= this.sceneImages.length;
  }

  shouldKeepMeshTextureOnRecycle() {
    return this.shouldStageUniqueMeshCreation();
  }

  requestTextureRebalance() {
    this.needsTextureRebalance = true;

    if (!this.motionStarted || this.isDestroyed) {
      return;
    }

    clearTimeout(this.textureRebalanceTimer);
    this.textureRebalanceTimer = globalThis.setTimeout(() => {
      this.textureRebalanceTimer = null;

      if (this.isDestroyed || !this.motionStarted) {
        return;
      }

      this.needsTextureRebalance = false;
      this.rebalanceTextureAssignments();
    }, this.config?.textureRebalanceDelay ?? 700);
  }

  rebalanceTextureAssignments() {
    if (!this.meshes.size || !this.texturePool.length) {
      return false;
    }

    let changed = false;
    const maxPasses = this.meshes.size;

    for (let pass = 0; pass < maxPasses; pass += 1) {
      const repeatLimit = this.getTextureRepeatLimit();
      const overusedKey = [...this.getTextureUsageCounts()]
        .find(([, count]) => count > repeatLimit)?.[0];

      if (!overusedKey) {
        break;
      }

      const mesh = this.getRebalanceMeshForTexture(overusedKey);

      if (!mesh) {
        break;
      }

      const next = this.pickTextureForSlot(
        mesh.userData.loopSlotIndex ?? pass,
        this.recycleCycle + pass,
        mesh.material.map,
        mesh
      );

      if (!next || getTextureEntryKey(next) === overusedKey) {
        break;
      }

      changed = this.assignTextureToMesh(mesh, next) || changed;
    }

    return changed;
  }

  getRebalanceMeshForTexture(textureKey) {
    return Array.from(this.meshes.values())
      .filter((mesh) => getMeshTextureKey(mesh, this.texturePool) === textureKey)
      .sort(compareRebalanceMeshes)[0];
  }

  assignTextureToMesh(mesh, poolEntry) {
    if (!mesh || !poolEntry || mesh.material?.map === poolEntry.texture) {
      return false;
    }

    mesh.userData.textureItem = poolEntry.item;
    mesh.userData.aspectRatio = poolEntry.aspectRatio ?? mesh.userData.aspectRatio ?? 1;
    mesh.material.map = poolEntry.texture;
    mesh.material.needsUpdate = true;

    if (this.camera && mesh.userData.slot) {
      this.applyPlaneGeometry(mesh);
    }

    return true;
  }

  applyPlaneGeometry(mesh) {
    const slot = mesh.userData.slot;
    const visual = getTunnelVisualState(slot, this.tunnel);
    const scale = slot.baseScale * visual.scaleMultiplier;
    const aspectRatio = mesh.userData.aspectRatio || 1;

    mesh.position.set(slot.x, slot.y, slot.z);
    mesh.rotation.set(0, 0, 0);
    mesh.scale.set(scale * aspectRatio, scale, 1);
    mesh.userData.currentScale = scale;
    mesh.material.opacity =
      visual.opacity *
      this.getCenterOpacityFactor(slot) *
      mesh.userData.reveal *
      mesh.userData.recycleReveal;
  }

  getCenterOpacityFactor(slot) {
    const farZ = this.tunnel?.farZ ?? DEFAULT_TUNNEL_CONFIG.farZ;
    const nearZ = this.tunnel?.nearZ ?? DEFAULT_TUNNEL_CONFIG.nearZ;
    const depthProgress = Math.min(1, Math.max(0, (slot.z - farZ) / (nearZ - farZ)));
    const depth = Math.max(0.2, -slot.z);
    const halfViewportHeight = depth * Math.tan((this.camera.fov * Math.PI) / 360);
    const halfViewportWidth = halfViewportHeight * this.camera.aspect;
    const x = Math.abs(slot.x / halfViewportWidth);
    const y = Math.abs(slot.y / halfViewportHeight);
    const clearance = Math.max(x / 0.72, y / 0.46);
    const centerFactor = Math.min(1, Math.max(0.14, 0.14 + clearance * 0.86));
    const release = smoothstep(0.26, 0.5, depthProgress);

    return centerFactor + (1 - centerFactor) * release;
  }

  getOccupiedSlots(excludedMesh = null) {
    return Array.from(this.meshes.values())
      .filter((mesh) => mesh !== excludedMesh)
      .map((mesh) => mesh.userData.slot);
  }

  revealMesh(mesh) {
    this.gsap?.killTweensOf?.(mesh.userData, 'reveal');

    if (!this.gsap) {
      mesh.userData.reveal = 1;
      this.applyPlaneGeometry(mesh);
      return;
    }

    this.gsap.to(mesh.userData, {
      reveal: 1,
      duration: this.reducedMotion ? 0 : 1.1,
      delay: 0,
      ease: 'power2.out',
    });
  }
}

export function getHeroRenderViewport(width, contentHeight, options = {}) {
  const bleedRatio = Math.max(
    0,
    Math.min(0.4, options.renderBleedBottomRatio ?? 0.18)
  );
  const bleedXRatio = Math.max(
    0,
    Math.min(0.4, options.renderBleedXRatio ?? 0.12)
  );
  const bleedX = Math.round(width * bleedXRatio);
  const bleedBottom = Math.round(contentHeight * bleedRatio);

  return {
    width,
    contentWidth: width,
    contentHeight,
    bleedX,
    bleedBottom,
    renderWidth: width + bleedX * 2,
    renderHeight: contentHeight + bleedBottom,
  };
}

function getTextureAspectRatio(texture, fallbackAspectRatio) {
  const image = texture?.image;
  const width = image?.naturalWidth ?? image?.videoWidth ?? image?.width;
  const height = image?.naturalHeight ?? image?.videoHeight ?? image?.height;

  if (width && height) {
    return width / height;
  }

  return fallbackAspectRatio || 1;
}

function getTextureEntryKey(entry) {
  return entry?.textureKey ?? entry?.item?.textureKey ?? entry?.item?.resource?.textureKey;
}

function getUniqueSceneTextureCount(sceneImages = []) {
  return new Set(sceneImages.map((item) => item.textureKey).filter(Boolean)).size;
}

function getMeshTextureKey(mesh, texturePool = []) {
  const userDataKey =
    mesh?.userData?.textureItem?.textureKey ??
    mesh?.userData?.textureItem?.resource?.textureKey;

  if (userDataKey) {
    return userDataKey;
  }

  const texture = mesh?.material?.map;
  const match = texturePool.find((entry) => entry.texture === texture);

  return getTextureEntryKey(match);
}

function compareRebalanceMeshes(first, second) {
  const firstOpacity = Number(first?.material?.opacity ?? 1);
  const secondOpacity = Number(second?.material?.opacity ?? 1);

  if (firstOpacity !== secondOpacity) {
    return firstOpacity - secondOpacity;
  }

  return Number(first?.userData?.slot?.z ?? 0) - Number(second?.userData?.slot?.z ?? 0);
}

function toPositiveInteger(value, fallback = 1) {
  const number = Math.floor(Number(value));

  if (Number.isFinite(number) && number > 0) {
    return number;
  }

  return fallback;
}

function smoothstep(edge0, edge1, value) {
  const x = Math.min(1, Math.max(0, (value - edge0) / (edge1 - edge0)));

  return x * x * (3 - 2 * x);
}

function mixInteger(value) {
  let state = value | 0;

  state ^= state >>> 16;
  state = Math.imul(state, 0x7feb352d);
  state ^= state >>> 15;
  state = Math.imul(state, 0x846ca68b);
  state ^= state >>> 16;

  return state >>> 0;
}
